import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import chunk from "lodash/chunk";
import classNames from "classnames";
import { Box, styled, useMediaQuery } from "~/common/mui";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import throttle from "lodash/throttle";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export enum DesktopContainerTypes {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
}

export enum MobileContainerTypes {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL_SCROLL",
  CAROUSEL = "CAROUSEL",
}

export interface CMSTeasersProps<T extends { index: number }> {
  teasers: T[];
  scrollType?: DesktopContainerTypes;
  itemsPerRow?: number;
  mobileScrollType?: MobileContainerTypes;
  mobileItemsPerRow?: number;
  className?: string;
  renderTeaserDetailsContainer: (validIds: number[]) => ReactNode;
  renderTeaser: (teaser: T) => ReactNode;
}

export function CMSTeaserContainer<T extends { index: number }>({
  scrollType: desktopScrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow: desktopItemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
  teasers,
  className,
  renderTeaserDetailsContainer,
  renderTeaser,
}: CMSTeasersProps<T>) {
  const isDesktop = useIsMinDesktopSize();
  const prevRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLDivElement | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const teasersWithId = teasers.map((teaser, index) => ({ ...teaser, id: index }));

  const containerRef = useRef<HTMLDivElement>(null);
  const isTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);

  const [maxTeasersPerRow, setMaxTeasersPerRow] = useState(isDesktop ? desktopItemsPerRow : mobileItemsPerRow);

  useEffect(() => {
    const fn = throttle(() => {
      if (isDesktop) {
        setMaxTeasersPerRow(desktopItemsPerRow);
      } else if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width === 0) {
          requestAnimationFrame(fn);
          return;
        }
        const calculatedTeasersPerRow = Math.floor(width / 228);
        setMaxTeasersPerRow(
          Math.max(calculatedTeasersPerRow > desktopItemsPerRow ? desktopItemsPerRow : calculatedTeasersPerRow, 1),
        );
      } else {
        setMaxTeasersPerRow((prev) => (prev && prev > 0 ? prev : 1));
      }
    }, 100);
    window.addEventListener("resize", fn);
    window.addEventListener("orientationchange", fn);
    fn();
    return () => {
      window.removeEventListener("resize", fn);
      window.removeEventListener("orientationchange", fn);
    };
  }, [isDesktop, isTablet, desktopItemsPerRow]);

  let itemStyle: CSSProperties | undefined = undefined;

  // Deprecated ?
  const scrollType: string = isDesktop ? desktopScrollType : mobileScrollType;

  if (scrollType !== MobileContainerTypes.HORIZONTAL) {
    itemStyle = {
      width: `calc((100% / ${maxTeasersPerRow})`,
    };
  }

  const useCarousel =
    (isDesktop && desktopScrollType === DesktopContainerTypes.CAROUSEL) ||
    (!isDesktop && mobileScrollType === MobileContainerTypes.CAROUSEL);

  const containerCls = `scha__teasers__elements scha__teasers__elements--${scrollType}`;
  const elementCls = `scha__teasers__element scha__teasers__element--${scrollType}`;

  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(0);
    }
  }, [teasers]);

  useEffect(() => {
    if (!useCarousel || !swiperRef.current) {
      return;
    }

    const swiper = swiperRef.current;

    // Pagination
    if (paginationRef.current && swiper.params.pagination && typeof swiper.params.pagination !== "boolean") {
      swiper.params.pagination.el = paginationRef.current;

      swiper.pagination.init();
      swiper.pagination.render();
      swiper.pagination.update();
    }

    // Navigation
    if (
      prevRef.current &&
      nextRef.current &&
      swiper.params.navigation &&
      typeof swiper.params.navigation !== "boolean"
    ) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;

      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [useCarousel, maxTeasersPerRow, teasers]);

  let data;
  if (useCarousel) {
    const spaceBetween = isDesktop ? 24 : isTablet ? 16 : 8;
    data = (
      <Swiper
        modules={[Pagination, Navigation]}
        pagination={{
          el: paginationRef.current,
          clickable: true,
        }}
        navigation={true}
        onBeforeInit={(swiper) => {
          if (typeof swiper.params.navigation !== "boolean") {
            swiper.params.navigation!.prevEl = prevRef.current;
            swiper.params.navigation!.nextEl = nextRef.current;
          }
          if (typeof swiper.params.pagination !== "boolean") {
            swiper.params.pagination!.el = paginationRef.current;
          }
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        slidesPerView={maxTeasersPerRow}
        slidesPerGroup={maxTeasersPerRow}
        spaceBetween={spaceBetween}
        className={containerCls}
      >
        {teasersWithId.map((item) => (
          <SwiperSlide
            key={`CMSTeaser-${item.index}`}
            className={classNames(elementCls, "scha__teasers__swiper-slide")}
          >
            {renderTeaser(item)}
          </SwiperSlide>
        ))}
      </Swiper>
    );
  } else {
    let rows;
    if (scrollType === MobileContainerTypes.HORIZONTAL) {
      rows = [teasersWithId];
    } else {
      rows = chunk(teasersWithId, maxTeasersPerRow);
    }
    data = (
      <>
        {rows.map((row, containerIndex) => (
          <div className={"scha__teasers__group"} key={`scha__teasers__level--${containerIndex}`}>
            <ul className={containerCls}>
              {row.map((item) => (
                <li key={item.id} style={itemStyle} className={elementCls}>
                  {renderTeaser(item)}
                </li>
              ))}
            </ul>
            {renderTeaserDetailsContainer(row.map((t) => t.index))}
          </div>
        ))}
      </>
    );
  }

  if (!teasers.length) {
    return null;
  }

  const hasPages = Math.ceil(teasers.length / maxTeasersPerRow) > 1;

  return (
    <StyledTeaserContainer
      className={classNames(
        `scha__teasers scha__teasers--${scrollType}`,
        {
          "scha__teasers--isDesktop": isDesktop,
          "scha__teasers--isTablet": isTablet,
        },
        className,
      )}
      ref={containerRef}
    >
      {data}
      {useCarousel && hasPages && (
        <div className="swiper-pagination-wrapper">
          <div ref={prevRef} className="swiper-button swiper-button-prev">
            <ChevronLeftIcon color="primary" fontSize="medium" />
          </div>
          <div ref={paginationRef} className="swiper-pagination-bullet-wrapper" />
          <div ref={nextRef} className="swiper-button swiper-button-next">
            <ChevronRightIcon color="primary" fontSize="medium" />
          </div>
        </div>
      )}
    </StyledTeaserContainer>
  );
}

const StyledTeaserContainer = styled(Box)(({ theme }) => ({
  "& .swiper": {
    paddingBottom: "24px", // 16px gap + 8px dot height
  },
  "& .swiper-pagination": {
    bottom: "0 !important",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
  },
  "& .swiper-pagination-bullet": {
    width: "8px",
    height: "8px",
    margin: "0 !important",
    borderRadius: "50%",
    opacity: 1,
    background: theme.palette.text.primary,
    transition: "background 0.2s",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  "& .swiper-pagination-bullet-active": {
    background: theme.palette.primary.main,
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  },
  "& .swiper-pagination-wrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
  },
  "& .swiper-pagination-bullet-wrapper": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  "& .swiper-pagination-bullets.swiper-pagination-horizontal": {
    width: "auto",
  },
  "& .swiper-button": {
    color: theme.palette.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  "& .swiper-button-disabled": {
    opacity: "50%",
  },
}));
